/**
 * Skin Issues Configuration by Plan Tier
 * 
 * This file defines which skin issues can be detected or inferred for each plan tier.
 * All language is non-medical and uses safe terminology like "prone to", "patterns", "indicators", "tendencies".
 */

export type PlanTier = 'plus' | 'pro' | 'elite';
export type DetectionMethod = 'detected' | 'inferred';

export interface SkinIssue {
  id: string;
  displayName: string;
  description: string;
  planTier: PlanTier;
  detectionMethod: DetectionMethod;
  category: 'acne' | 'oil' | 'barrier' | 'sensitivity' | 'texture' | 'pigmentation' | 'aging' | 'environmental';
  icon?: string;
}

/**
 * All skin issues organized by plan tier
 */
export const SKIN_ISSUES: SkinIssue[] = [
  // ========== PLUS PLAN (Acne & Oil Focus) ==========
  {
    id: 'whiteheads',
    displayName: 'Whiteheads',
    description: 'Small, closed comedones that appear as white bumps',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'acne',
    icon: '⚪',
  },
  {
    id: 'blackheads',
    displayName: 'Blackheads',
    description: 'Open comedones with darkened appearance',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'acne',
    icon: '⚫',
  },
  {
    id: 'papules',
    displayName: 'Papules',
    description: 'Small, raised bumps without pus',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'acne',
    icon: '🔴',
  },
  {
    id: 'pustules',
    displayName: 'Pustules',
    description: 'Inflamed bumps with visible pus',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'acne',
    icon: '🟡',
  },
  {
    id: 'mild_inflammatory_acne',
    displayName: 'Mild Inflammatory Acne',
    description: 'Early-stage inflammatory acne patterns',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'acne',
    icon: '🔴',
  },
  {
    id: 'oily_skin',
    displayName: 'Oily Skin',
    description: 'Excess sebum production patterns',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'oil',
    icon: '💧',
  },
  {
    id: 'excess_sebum',
    displayName: 'Excess Sebum',
    description: 'Increased oil production indicators',
    planTier: 'plus',
    detectionMethod: 'inferred',
    category: 'oil',
    icon: '💧',
  },
  {
    id: 'enlarged_pores',
    displayName: 'Enlarged Pores',
    description: 'Visible pore size indicators',
    planTier: 'plus',
    detectionMethod: 'detected',
    category: 'oil',
    icon: '🔍',
  },

  // ========== PRO PLAN (Adds Barrier, Sensitivity, Texture) ==========
  {
    id: 'dry_skin',
    displayName: 'Dry Skin',
    description: 'Skin prone to dryness and tightness',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'barrier',
    icon: '🏜️',
  },
  {
    id: 'dehydrated_skin',
    displayName: 'Dehydrated Skin',
    description: 'Lack of moisture indicators',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'barrier',
    icon: '💨',
  },
  {
    id: 'flaking',
    displayName: 'Flaking',
    description: 'Visible flaking or peeling patterns',
    planTier: 'pro',
    detectionMethod: 'detected',
    category: 'barrier',
    icon: '❄️',
  },
  {
    id: 'tightness',
    displayName: 'Tightness',
    description: 'Skin tightness indicators',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'barrier',
    icon: '🔒',
  },
  {
    id: 'rough_texture',
    displayName: 'Rough Texture',
    description: 'Uneven or rough skin texture patterns',
    planTier: 'pro',
    detectionMethod: 'detected',
    category: 'texture',
    icon: '🌊',
  },
  {
    id: 'compromised_barrier',
    displayName: 'Compromised Skin Barrier Indicators',
    description: 'Patterns suggesting barrier function concerns',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'barrier',
    icon: '🛡️',
  },
  {
    id: 'sensitive_skin_tendencies',
    displayName: 'Sensitive Skin Tendencies',
    description: 'Patterns indicating skin prone to sensitivity',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'sensitivity',
    icon: '🌡️',
  },
  {
    id: 'redness_non_medical',
    displayName: 'Redness Patterns',
    description: 'Visible redness indicators (non-medical)',
    planTier: 'pro',
    detectionMethod: 'detected',
    category: 'sensitivity',
    icon: '🔴',
  },
  {
    id: 'product_irritation_patterns',
    displayName: 'Product Irritation Patterns',
    description: 'Indicators suggesting potential product sensitivity',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'sensitivity',
    icon: '⚠️',
  },
  {
    id: 'over_exfoliation_indicators',
    displayName: 'Over-Exfoliation Indicators',
    description: 'Patterns suggesting excessive exfoliation',
    planTier: 'pro',
    detectionMethod: 'inferred',
    category: 'barrier',
    icon: '🔄',
  },
  {
    id: 'uneven_texture',
    displayName: 'Uneven Texture',
    description: 'Irregular skin surface patterns',
    planTier: 'pro',
    detectionMethod: 'detected',
    category: 'texture',
    icon: '📊',
  },
  {
    id: 'early_pih',
    displayName: 'Early Post-Acne Marks',
    description: 'Mild post-inflammatory hyperpigmentation patterns',
    planTier: 'pro',
    detectionMethod: 'detected',
    category: 'pigmentation',
    icon: '🔸',
  },
  {
    id: 'dullness',
    displayName: 'Dullness',
    description: 'Lack of radiance or brightness indicators',
    planTier: 'pro',
    detectionMethod: 'detected',
    category: 'texture',
    icon: '🌑',
  },

  // ========== ELITE PLAN (Comprehensive Skin Health Insights) ==========
  {
    id: 'moderate_acne_patterns',
    displayName: 'Moderate Acne Patterns',
    description: 'Moderate inflammatory acne distribution patterns',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'acne',
    icon: '🔴',
  },
  {
    id: 'hormonal_acne_distribution',
    displayName: 'Hormonal Acne Distribution Patterns',
    description: 'Acne patterns suggesting hormonal influences',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'acne',
    icon: '⚖️',
  },
  {
    id: 'cystic_like_tendencies',
    displayName: 'Cystic-Like Acne Tendencies',
    description: 'Patterns indicating deeper, more persistent acne (non-diagnostic)',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'acne',
    icon: '🔴',
  },
  {
    id: 'acne_scarring_rolling',
    displayName: 'Rolling Acne Scarring Appearance',
    description: 'Visible rolling scar patterns from previous acne',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'texture',
    icon: '🌊',
  },
  {
    id: 'acne_scarring_boxcar',
    displayName: 'Boxcar Acne Scarring Appearance',
    description: 'Visible boxcar scar patterns from previous acne',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'texture',
    icon: '📦',
  },
  {
    id: 'hyperpigmentation',
    displayName: 'Hyperpigmentation',
    description: 'Darkened skin areas or patches',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'pigmentation',
    icon: '🌑',
  },
  {
    id: 'pih',
    displayName: 'Post-Inflammatory Hyperpigmentation (PIH)',
    description: 'Dark marks following inflammation or injury',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'pigmentation',
    icon: '🔸',
  },
  {
    id: 'sun_spots',
    displayName: 'Sun Spots / Age Spots',
    description: 'Sun-related pigmentation patterns',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'pigmentation',
    icon: '☀️',
  },
  {
    id: 'uneven_skin_tone',
    displayName: 'Uneven Skin Tone',
    description: 'Irregular pigmentation distribution',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'pigmentation',
    icon: '🎨',
  },
  {
    id: 'melasma_like_patterns',
    displayName: 'Melasma-Like Pigmentation Patterns',
    description: 'Informational patterns suggesting melasma-like appearance (informational only)',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'pigmentation',
    icon: '🌓',
  },
  {
    id: 'fine_lines',
    displayName: 'Fine Lines',
    description: 'Early signs of fine lines',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'aging',
    icon: '📏',
  },
  {
    id: 'wrinkles',
    displayName: 'Wrinkles',
    description: 'Visible wrinkle patterns',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'aging',
    icon: '📐',
  },
  {
    id: 'loss_of_elasticity',
    displayName: 'Loss of Elasticity Indicators',
    description: 'Patterns suggesting reduced skin elasticity',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'aging',
    icon: '🔽',
  },
  {
    id: 'skin_laxity',
    displayName: 'Skin Laxity Indicators',
    description: 'Patterns suggesting skin laxity',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'aging',
    icon: '📉',
  },
  {
    id: 'crepey_texture',
    displayName: 'Crepey Texture',
    description: 'Fine, crinkled texture patterns',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'aging',
    icon: '📄',
  },
  {
    id: 'enlarged_pore_severity',
    displayName: 'Enlarged Pore Severity Grading',
    description: 'Detailed pore size and severity assessment',
    planTier: 'elite',
    detectionMethod: 'detected',
    category: 'oil',
    icon: '🔍',
  },
  {
    id: 'chronic_redness_patterns',
    displayName: 'Chronic Redness Patterns',
    description: 'Persistent redness indicators',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'sensitivity',
    icon: '🔴',
  },
  {
    id: 'eczema_prone_indicators',
    displayName: 'Eczema-Prone Skin Indicators',
    description: 'Patterns suggesting skin prone to eczema-like concerns',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'sensitivity',
    icon: '🌡️',
  },
  {
    id: 'rosacea_prone_indicators',
    displayName: 'Rosacea-Prone Skin Indicators',
    description: 'Patterns suggesting skin prone to rosacea-like concerns',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'sensitivity',
    icon: '🌡️',
  },
  {
    id: 'uv_damage_indicators',
    displayName: 'UV Damage Indicators',
    description: 'Patterns suggesting sun damage',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'environmental',
    icon: '☀️',
  },
  {
    id: 'environmental_stress_markers',
    displayName: 'Environmental Stress Markers',
    description: 'Indicators of environmental damage or stress',
    planTier: 'elite',
    detectionMethod: 'inferred',
    category: 'environmental',
    icon: '🌍',
  },
];

/**
 * Get all skin issues available for a specific plan tier
 */
export function getIssuesForPlan(planTier: PlanTier): SkinIssue[] {
  const tierOrder: PlanTier[] = ['plus', 'pro', 'elite'];
  const currentTierIndex = tierOrder.indexOf(planTier);
  
  return SKIN_ISSUES.filter(issue => {
    const issueTierIndex = tierOrder.indexOf(issue.planTier);
    return issueTierIndex <= currentTierIndex;
  });
}

/**
 * Get issues by category for a specific plan tier
 */
export function getIssuesByCategory(planTier: PlanTier): Record<string, SkinIssue[]> {
  const issues = getIssuesForPlan(planTier);
  const categorized: Record<string, SkinIssue[]> = {};
  
  issues.forEach(issue => {
    if (!categorized[issue.category]) {
      categorized[issue.category] = [];
    }
    categorized[issue.category].push(issue);
  });
  
  return categorized;
}

/**
 * Get issues by detection method for a specific plan tier
 */
export function getIssuesByDetectionMethod(planTier: PlanTier): {
  detected: SkinIssue[];
  inferred: SkinIssue[];
} {
  const issues = getIssuesForPlan(planTier);
  
  return {
    detected: issues.filter(issue => issue.detectionMethod === 'detected'),
    inferred: issues.filter(issue => issue.detectionMethod === 'inferred'),
  };
}

/**
 * Check if a specific issue is available for a plan tier
 */
export function isIssueAvailableForPlan(issueId: string, planTier: PlanTier): boolean {
  const issue = SKIN_ISSUES.find(i => i.id === issueId);
  if (!issue) return false;
  
  const tierOrder: PlanTier[] = ['plus', 'pro', 'elite'];
  const currentTierIndex = tierOrder.indexOf(planTier);
  const issueTierIndex = tierOrder.indexOf(issue.planTier);
  
  return issueTierIndex <= currentTierIndex;
}

/**
 * Get plan tier required for a specific issue
 */
export function getRequiredPlanTier(issueId: string): PlanTier | null {
  const issue = SKIN_ISSUES.find(i => i.id === issueId);
  return issue ? issue.planTier : null;
}

/**
 * Plan tier display names
 */
export const PLAN_TIER_NAMES: Record<PlanTier, string> = {
  plus: 'GlowLens Plus',
  pro: 'GlowLens Pro',
  elite: 'GlowLens Elite',
};

/**
 * Disclaimers for each plan tier
 */
export const PLAN_DISCLAIMERS: Record<PlanTier, string | null> = {
  plus: null, // No disclaimer needed for basic acne/oil detection
  pro: 'Insights are informational and not medical diagnoses.',
  elite: 'Insights are informational and not medical diagnoses. For medical concerns, please consult a healthcare professional.',
};

